import { useEffect, useMemo } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { AreaChart, XAxis, YAxis, Tooltip, Area } from 'recharts';
import { formatNumber } from '../../utils';
import { PERMISSIONS } from '../../constants';
import { getChainId, getEmployeeType, getAttendantId } from '../../lib';
import useDashboardContext from '../../hooks/useDashboardContext';
import { DashboardWrapper, RotatedAxisTick } from '../../components';
import moment from 'moment';
import { EmployeeType } from '../../models';

const colors = {
    area01: "#79CF62",
    area02: "#7869DF",
};

const yAxisDomain = [0, 100];
const yAxisTickFormatter = (value) => `${value}%`;
const tooltipFormatter = (value) => `${formatNumber(value)}% dos abastecimentos`;

const formatToPercentage = (value, total) => {
    return ((value * 100) / total).toFixed(2);
};

export const FillingCompletelyXfillingLimit = () => {
    const dataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const {
        from, to, chain, origin, stationIds,
        fillingBehaviorData, fillingBehaviorLoading,
        setFillingBehaviorData, setFillingBehaviorLoading
    } = useDashboardContext();
    const notify = useNotify();

    const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

    useEffect(() => {
        if (fillingBehaviorLoading) return;

        const filter = { from, to, chainId, origin };
        if (stationIds && stationIds.length > 0) { filter.placeIds = stationIds; }
        if (getEmployeeType() === EmployeeType.attendant) { filter.attendantId = getAttendantId(); }

        if (permissions && permissions.includes(PERMISSIONS.FILLINS_CHART)) {
            setFillingBehaviorLoading(true);
            dataProvider.getList('dashboard/filling-behavior', { filter, chainId })
                .then(response => { setFillingBehaviorData(response.data); })
                .catch((e) => {
                    notify('Erro ao carregar dados de comportamento de abastecimento', { type: 'warning' });
                    setFillingBehaviorData(null);
                })
                .finally(() => { setFillingBehaviorLoading(false); });
        }
    }, [permissions, from, to, chain, origin, stationIds]);

    const chartData = useMemo(() => {
        if (!fillingBehaviorData?.perDay) return null;

        return fillingBehaviorData.perDay.map((element) => ({
            ...element,
            date: moment(element.id, 'YYYY-MM-DD').format('DD/MM/YYYY'),
            filling_completely: formatToPercentage(element.filling_completely, element.total),
            filling_limit: formatToPercentage(element.filling_limit, element.total),
        }));
    }, [fillingBehaviorData]);

    const chartContent = useMemo(() => (
        <AreaChart data={chartData || []}>
            <YAxis domain={yAxisDomain} tickFormatter={yAxisTickFormatter} />
            <XAxis dataKey="date" tick={<RotatedAxisTick fontSize={12} />} height={60} />
            <Tooltip formatter={tooltipFormatter} />
            <Area
                type="monotone"
                name='Encher Tanque'
                dataKey="filling_completely"
                stackId="1"
                stroke={colors.area01}
                fill={colors.area01}
                isAnimationActive={true}
            />
            <Area
                type="monotone"
                name="Não encher tanque"
                dataKey="filling_limit"
                stackId="1"
                stroke={colors.area02}
                fill={colors.area02}
                isAnimationActive={true}
            />
        </AreaChart>
    ), [chartData]);

    return (
        <DashboardWrapper
            title='Encher tanque vs Não encher tanque'
            permission={PERMISSIONS.FILLINS_CHART}
            aspectRatio={6.0 / 2.5}
            count={chartData?.length || 0}
            loading={!fillingBehaviorData && fillingBehaviorLoading}
            content={chartContent}
        />
    );
}
